import React from 'react';

import { CheckCircle2, XCircle, AlertCircle, ShieldCheck, Hourglass, FileSignature, FolderArchive } from 'lucide-react';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const STATUT_META = {
  brouillon:       { label: 'Brouillon',       color: 'bg-slate-600 text-white border-slate-800 font-semibold', Icon: Hourglass },
  transmis_domium: { label: 'Transmis DOMIUM', color: 'bg-blue-700 text-white border-blue-900 font-semibold',   Icon: ShieldCheck },
  signe:           { label: 'Signé',            color: 'bg-emerald-700 text-white border-emerald-900 font-semibold', Icon: FileSignature },
  archive:         { label: 'Archivé',          color: 'bg-gray-700 text-white border-gray-900 font-semibold',  Icon: FolderArchive },
};

export const GUARD_STATUS_META = {
  ok:              { Icon: CheckCircle2, color: 'text-emerald-600', badge: 'bg-emerald-700 text-white border-emerald-900 font-semibold' },
  missing:         { Icon: AlertCircle,  color: 'text-amber-600',   badge: 'bg-amber-600 text-white border-amber-800 font-semibold' },
  non_applicable:  { Icon: XCircle,      color: 'text-gray-400',    badge: 'bg-slate-600 text-white border-slate-800 font-semibold' },
};

export const getAuthHeader = () => {
  // L'admin Super Admin stocke son JWT dans `admin_token` (AdminAuthContext).
  // Les utilisateurs classiques utilisent `token`. On accepte les deux.
  const t = localStorage.getItem('admin_token')
         || localStorage.getItem('token')
         || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * V38.16 — Checklist de conformité du projet de mise en conformité CCMI.
 *
 * Chaque item définit un champ obligatoire (ou très fortement attendu) du
 * projet de contrat corrigé. La fonction `check(draft)` doit retourner
 * `true` quand le champ est valide.
 *
 * L'UI calcule en temps réel l'état COMPLET vs INCOMPLET et liste les
 * éléments manquants — pas d'appel backend, idempotent côté React.
 */
export const CCMI_COMPLIANCE_CHECKLIST = [
  { key: 'mo_nom',           label: "Maître d'ouvrage — Nom",                check: (d) => !!(d?.maitre_ouvrage?.nom || '').trim() },
  { key: 'mo_email',         label: "Maître d'ouvrage — Email valide",       check: (d) => /\S+@\S+\.\S+/.test((d?.maitre_ouvrage?.email || '').trim()) },
  { key: 'mo_telephone',     label: "Maître d'ouvrage — Téléphone",          check: (d) => !!(d?.maitre_ouvrage?.telephone || '').trim() },
  { key: 'mo_adresse',       label: "Maître d'ouvrage — Adresse complète",   check: (d) => ((d?.maitre_ouvrage?.adresse || '').trim().length >= 5) },
  { key: 'constructeur_rs',  label: 'Constructeur — Raison sociale',         check: (d) => !!(d?.constructeur?.raison_sociale || '').trim() },
  { key: 'constructeur_siret', label: 'Constructeur — SIRET (14 chiffres)',  check: (d) => /^\d{14}$/.test((d?.constructeur?.siret || '').replace(/\s+/g, '')) },
  { key: 'garant',           label: 'Garant — Garantie de livraison (art. L231-6 CCH)', check: (d) => !!((d?.garantie_livraison || d?.constructeur?.garant || '')).trim() },
  { key: 'terrain_adresse',  label: 'Terrain — Adresse',                     check: (d) => !!(d?.terrain?.adresse || '').trim() },
  { key: 'terrain_surface',  label: 'Terrain — Surface (m²)',                check: (d) => Number(d?.terrain?.surface_m2 || 0) > 0 },
  { key: 'terrain_parcelle', label: 'Terrain — Parcelle cadastrale',         check: (d) => !!(d?.terrain?.parcelle || '').trim() },
  { key: 'prix_ttc',         label: 'Prix forfaitaire TTC (€)',              check: (d) => Number(d?.prix_ttc || 0) > 0 },
  { key: 'type_contrat',     label: 'Type de contrat (avec/sans plan)',      check: (d) => ['avec_plan', 'sans_plan'].includes(d?.type_contrat) },
  { key: 'delai',            label: "Délai d'exécution",                     check: (d) => !!(d?.delai_execution || '').trim() },
  { key: 'penalites',        label: 'Pénalités de retard',                   check: (d) => !!(d?.penalites_retard || '').trim() },
  { key: 'financement',      label: 'Financement (prêts)',                   check: (d) => !!(d?.financement || '').trim() },
  { key: 'permis',           label: 'Permis de construire',                  check: (d) => !!(d?.permis || '').trim() },
  { key: 'assurance_do',     label: 'Assurance Dommages-Ouvrage (art. L242-1)', check: (d) => !!(d?.assurance_do || '').trim() },
];

export function computeComplianceStatus(draft) {
  if (!draft) {
    return { ok: false, missing: [], filled: [], score: 0, total: CCMI_COMPLIANCE_CHECKLIST.length };
  }
  const missing = [];
  const filled = [];
  for (const item of CCMI_COMPLIANCE_CHECKLIST) {
    try {
      if (item.check(draft)) filled.push(item);
      else missing.push(item);
    } catch {
      missing.push(item);
    }
  }
  return {
    ok: missing.length === 0,
    missing,
    filled,
    score: filled.length,
    total: CCMI_COMPLIANCE_CHECKLIST.length,
  };
}
